import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../supabaseClient';
import {
  useFxRates, useInterestRates, resolveSpot,
  totalLcPricingInr, calcFxRiskInr, calcEstimatedCfInr, calcBcCfInr, bcInterestInr,
  daysToDate, effectivePaymentDate, fmtCr, fmtL, fmtDateShort,
  INDEX_BADGE_COLORS, BENCHMARKS, CURRENCIES, LC_TYPES, HEDGE_STATUSES,calcCfFromHedges, deriveHedgeStatus, deriveHedgedAmount,
} from '../utils/importFinancingUtils';
import ImportFinancingDashboard from './ImportFinancingDashboard';
import NewLCModal from './NewLCModal';
import NewBCModal from './NewBCModal';
import LCActionModal from './LCActionModal';
import ExcelUploadModal from './ExcelUploadModal';

const USANCE_PERIODS = [30, 60, 90, 120, 150, 180];
const USANCE_FROM    = ['Bill of Lading', 'Sight of Documents', 'Port of Discharge'];
const HEDGE_BADGE    = { 'Fully Hedged': 'bg-green-900 text-green-300', 'Partially Hedged': 'bg-amber-900 text-amber-300', 'Unhedged': 'bg-red-900 text-red-300' };

export default function ImportFinancingPage({ companyId, onBack, onNavigateToHedge }) {
  const [activeTab,     setActiveTab]     = useState('lc');
  const [lcRecords,     setLcRecords]     = useState([]);
  const [bcRecords,     setBcRecords]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showNewLC,     setShowNewLC]     = useState(false);
  const [showNewBC,     setShowNewBC]     = useState(false);
  const [showUploadLC,  setShowUploadLC]  = useState(false);
  const [showUploadBC,  setShowUploadBC]  = useState(false);
  const [actionRecord,  setActionRecord]  = useState(null);
  const [editLC,        setEditLC]        = useState(null);
  const [editBC,        setEditBC]        = useState(null);
  const [deleteRec,     setDeleteRec]     = useState(null);
  const [hedgePositions, setHedgePositions] = useState([]);
  const [showArchive, setShowArchive] = useState(false);

  const { rates: fxRates,      loading: ratesLoading } = useFxRates();
  const { rates: interestRates                        } = useInterestRates();

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

async function fetchAll() {
  setLoading(true);
  const [{ data: lc }, { data: bc }, { data: hp }] = await Promise.all([
    supabase.from('import_financing').select('*').eq('company_id', companyId).order('maturity_date', { ascending: true }),
    supabase.from('import_financing_bc').select('*').eq('company_id', companyId).order('repayment_date', { ascending: true }),
    supabase.from('hedge_positions').select('id,underlying_type,underlying_ref,notional_amount_num,rate_locked_num,trade_date,status').eq('company_id', companyId),
  ]);
  setLcRecords(lc || []);
  setBcRecords(bc || []);
  setHedgePositions(hp || []);
  setLoading(false);
}

  const activeLCs   = lcRecords.filter(r => r.status !== 'Settled' && r.status !== 'Converted to BC');
  const archivedLCs = lcRecords.filter(r => r.status === 'Settled' || r.status === 'Converted to BC' || daysToDate(effectivePaymentDate(r)) < 0);
  const activeBCs   = bcRecords.filter(r => r.status !== 'Settled');
  const archivedBCs = bcRecords.filter(r => r.status === 'Settled' || daysToDate(r.repayment_date) < 0);

  const rateIndicator = () => {
    if (ratesLoading || !fxRates['USDINR']) return null;
    return (
      <div className="flex items-center gap-1.5 text-xs text-teal-400">
        <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
        USD {fxRates['USDINR']?.toFixed(2)} · EUR {fxRates['EURINR']?.toFixed(2)} · GBP {fxRates['GBPINR']?.toFixed(2)}
      </div>
    );
  };

  const tabs = [
    { key: 'lc',      label: 'Letter of Credit' },
    { key: 'bc',      label: 'Buyers Credit'     },
    { key: 'summary', label: 'Dashboard'         },
  ];

  return (
    <div className="p-6 max-w-full">

      {/* Header */}
      <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          {onBack && <button onClick={onBack} className="text-gray-400 hover:text-white text-sm">← Back</button>}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-white">Import Financing</h1>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-400 text-amber-900">PRO</span>
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <p className="text-sm text-gray-400">LC &amp; Buyers Credit — pricing, exposure &amp; conversions</p>
              {rateIndicator()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'lc' && (<>
           <button onClick={() => setShowArchive(v => !v)}
  className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${showArchive ? 'border-amber-700 text-amber-400' : 'border-gray-700 text-gray-400 hover:text-white'}`}>
  {showArchive ? '← Active' : '📁 Archive'}
</button>
            <button onClick={() => setShowUploadLC(true)} className="px-3 py-1.5 text-sm border border-gray-700 text-gray-300 hover:text-white rounded-lg">↑ Upload</button>
            <button onClick={() => setShowNewLC(true)}    className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-500 text-white rounded-lg">+ New LC</button>
          </>)}
          {activeTab === 'bc' && (<>
            <button onClick={() => setShowArchive(v => !v)}
  className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${showArchive ? 'border-amber-700 text-amber-400' : 'border-gray-700 text-gray-400 hover:text-white'}`}>
  {showArchive ? '← Active' : '📁 Archive'}
</button>
            <button onClick={() => setShowUploadBC(true)} className="px-3 py-1.5 text-sm border border-gray-700 text-gray-300 hover:text-white rounded-lg">↑ Upload</button>
            <button onClick={() => setShowNewBC(true)}    className="px-3 py-1.5 text-sm bg-purple-700 hover:bg-purple-600 text-white rounded-lg">+ New BC</button>
          </>)}
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-1 mb-5">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-1.5 text-sm rounded-lg border transition-colors ${activeTab === t.key ? 'bg-gray-800 border-gray-600 text-white font-medium' : 'border-gray-700 text-gray-400 hover:text-gray-200'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Loading...</div>
      ) : (
        <>
          {activeTab === 'lc' && (
           <LCSection
  records={showArchive ? archivedLCs : activeLCs}
  archived={showArchive}
  fxRates={fxRates}
  interestRates={interestRates}
  hedgePositions={hedgePositions}
  onAction={(lc, mode) => setActionRecord({ lc, mode })}
  onEdit={lc => setEditLC(lc)}
  onDelete={lc => setDeleteRec({ type: 'lc', record: lc })}
 onAddHedge={onNavigateToHedge}
/>
          )}
          {activeTab === 'bc' && (
   <BCSection
  records={showArchive ? archivedBCs : activeBCs}
  archived={showArchive}
  fxRates={fxRates}
  interestRates={interestRates}
  hedgePositions={hedgePositions}
  onEdit={bc => setEditBC(bc)}
  onDelete={bc => setDeleteRec({ type: 'bc', record: bc })}
  onAddHedge={onNavigateToHedge}
/>
          )}
          {activeTab === 'summary' && (
            <ImportFinancingDashboard lcRecords={lcRecords} bcRecords={bcRecords} />
          )}
        </>
      )}

      {/* Modals */}
      {showNewLC    && <NewLCModal companyId={companyId} onClose={() => setShowNewLC(false)} onSaved={() => { setShowNewLC(false); fetchAll(); }} />}
      {showNewBC    && <NewBCModal companyId={companyId} onClose={() => setShowNewBC(false)} onSaved={() => { setShowNewBC(false); fetchAll(); }} />}
      {showUploadLC && <ExcelUploadModal type="lc" companyId={companyId} onClose={() => setShowUploadLC(false)} onSaved={() => { setShowUploadLC(false); fetchAll(); }} />}
      {showUploadBC && <ExcelUploadModal type="bc" companyId={companyId} onClose={() => setShowUploadBC(false)} onSaved={() => { setShowUploadBC(false); fetchAll(); }} />}
      {actionRecord && <LCActionModal lc={actionRecord.lc} mode={actionRecord.mode} fxRates={fxRates} interestRates={interestRates} onClose={() => setActionRecord(null)} onSaved={() => { setActionRecord(null); fetchAll(); }} />}
      {editLC       && <EditLCInline lc={editLC} interestRates={interestRates} onClose={() => setEditLC(null)} onSaved={() => { setEditLC(null); fetchAll(); }} />}
      {editBC       && <EditBCInline bc={editBC} interestRates={interestRates} onClose={() => setEditBC(null)} onSaved={() => { setEditBC(null); fetchAll(); }} />}
      {deleteRec    && <DeleteConfirm type={deleteRec.type} record={deleteRec.record} onClose={() => setDeleteRec(null)} onSaved={() => { setDeleteRec(null); fetchAll(); }} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LC SECTION
// ─────────────────────────────────────────────────────────────
function LCSection({ records, archived, fxRates, interestRates, hedgePositions, onAction, onEdit, onDelete, onAddHedge }) {
  const { rates: localFx } = useFxRates();
  const fx = Object.keys(fxRates).length > 0 ? fxRates : localFx;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const filtered = records.filter(r => {
    const d = effectivePaymentDate(r);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo   && d > dateTo)   return false;
    return true;
  });

  const active        = filtered.filter(r => r.status !== 'Settled');
  const totalExposure = active.reduce((s, r) => s + r.amount_fcy * resolveSpot(r.currency, fx, 0), 0);
  const totalPricing  = active.reduce((s, r) => s + totalLcPricingInr(r, fx), 0);
  const totalRisk = active.reduce((s, r) => {
  const hedgedAmt = deriveHedgedAmount(r, hedgePositions, 'lc');
  return s + calcFxRiskInr(r.amount_fcy, hedgedAmt, r.currency, fx, 0);
}, 0);
  const dueSoon       = active.filter(r => { const d = daysToDate(effectivePaymentDate(r)); return d >= 0 && d <= 30; }).length;

  function exportExcel() {
    const rows = filtered.map(r => ({
      'LC Ref':                    r.lc_ref,
      'Type':                      r.lc_type,
      'Beneficiary':               r.beneficiary,
      'Issuing Bank':              r.issuing_bank,
      'Currency':                  r.currency,
      'Amount (FCY)':              r.amount_fcy,
      'Issue Date':                r.issue_date,
      'Maturity Date':             r.maturity_date,
      'Usance Period (days)':      r.usance_period_days || '',
      'Usance From':               r.usance_from || '',
      'Effective Payment Date':    effectivePaymentDate(r),
      'Days to Maturity':          daysToDate(effectivePaymentDate(r)),
      'Commission Rate (%)':       r.commission_rate,
      'Issuance Fee (INR)':        r.issuance_fee_inr,
      'SWIFT Fee (INR)':           r.swift_fee_inr,
      'Negotiation Fee (INR)':     r.negotiation_fee_inr,
      'Amendment Fee (INR)':       r.amendment_fee_inr,
      'Other Charges (INR)':       r.other_fee_inr,
      'Total Pricing Cost (INR)':  Math.round(totalLcPricingInr(r, fx)),
      'Hedge Status':              r.hedge_status,
      'Hedged Amount (FCY)':       r.hedged_amount_fcy,
      'Contracted Rate (INR)':     r.contracted_rate_inr || '',
      'Live Spot Rate (INR)':      resolveSpot(r.currency, fx, 0),
      'FX Risk (INR) @ 2% stress': Math.round(calcFxRiskInr(r.amount_fcy, r.hedged_amount_fcy, r.currency, fx, 0)),
      'Est. Cash Flow (INR)':      Math.round(calcEstimatedCfInr(r, fx, interestRates)),
      'Status':                    r.status,
    }));
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0]).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'LC Records');
    XLSX.writeFile(wb, `Nebula_LC_${archived ? 'archived' : 'active'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  return (
    <div>
      {!archived && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total LC Exposure',  value: fmtCr(totalExposure), sub: `${active.length} active LCs` },
            { label: 'Total Pricing Cost', value: fmtL(totalPricing),   sub: 'commission + bank charges' },
            { label: 'Unhedged FX Risk',   value: fmtCr(totalRisk),     sub: '2% stress on unhedged FCY', warn: true },
            { label: 'Maturing ≤ 30 Days', value: `${dueSoon} LCs`,     sub: 'action required', warn: dueSoon > 0 },
          ].map(k => (
            <div key={k.label} className="bg-gray-800 rounded-xl p-4">
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">{k.label}</div>
              <div className={`text-xl font-semibold ${k.warn ? 'text-amber-400' : 'text-white'}`}>{k.value}</div>
              <div className="text-xs text-gray-500 mt-1">{k.sub}</div>
            </div>
          ))}
        </div>
      )}

      {archived && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-900/20 border border-amber-800 rounded-xl mb-4 text-xs text-amber-400">
          📁 Showing archived LCs — settled, converted to BC, and expired records
        </div>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <span className="text-sm font-medium text-white">{archived ? 'Archived LC Records' : 'LC Records'}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Maturity:</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded-lg text-white" />
            <span className="text-xs text-gray-500">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded-lg text-white" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs text-red-400 hover:text-red-300">✕ Clear</button>
            )}
            <button onClick={exportExcel} className="px-2 py-1 text-xs border border-gray-700 text-gray-300 hover:text-white rounded-lg">↓ Excel</button>
            <span className="text-xs text-gray-500">{filtered.length} records</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-800 text-gray-400 text-left">
                <th className="px-3 py-2.5 font-medium">LC Ref</th>
                <th className="px-3 py-2.5 font-medium">Type</th>
                <th className="px-3 py-2.5 font-medium">Beneficiary</th>
                <th className="px-3 py-2.5 font-medium">Amount</th>
                <th className="px-3 py-2.5 font-medium">Maturity</th>
                <th className="px-3 py-2.5 font-medium">Bank</th>
                <th className="px-3 py-2.5 font-medium">Pricing</th>
                <th className="px-3 py-2.5 font-medium">Hedge</th>
                <th className="px-3 py-2.5 font-medium" title="Unhedged FCY × Live Spot × 2%">FX Risk ⓘ</th>
                <th className="px-3 py-2.5 font-medium">Est. CF (INR)</th>
                <th className="px-3 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-500">{archived ? 'No archived LCs' : 'No active LCs'}</td></tr>
              ) : filtered.map(r => (
               <LCRow key={r.id} r={r} fx={fx} interestRates={interestRates} archived={archived}
  hedgePositions={hedgePositions}
  onAction={onAction} onEdit={onEdit} onDelete={onDelete} onAddHedge={onAddHedge} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function LCRow({ r, fx, interestRates, archived, hedgePositions, onAction, onEdit, onDelete, onAddHedge }) {
  const effDate     = effectivePaymentDate(r);
  const days        = daysToDate(effDate);
  const settled     = r.status === 'Settled';
  const pricing     = totalLcPricingInr(r, fx);
  const hedgeStatus = deriveHedgeStatus(r, hedgePositions, 'lc');
  const hedgedAmt   = deriveHedgedAmount(r, hedgePositions, 'lc');
  const risk        = calcFxRiskInr(r.amount_fcy, hedgedAmt, r.currency, fx, 0);
  const cf          = calcCfFromHedges(r, hedgePositions, fx, interestRates, 'lc');

  return (
    <tr className={`border-t border-gray-800 hover:bg-gray-800/40 transition-colors ${archived || settled ? 'opacity-60' : ''}`}>
      <td className="px-3 py-2.5 font-medium text-white">{r.lc_ref}</td>
      <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-xs bg-blue-900 text-blue-300">{r.lc_type}</span></td>
      <td className="px-3 py-2.5 text-gray-300">{r.beneficiary}</td>
      <td className="px-3 py-2.5 text-gray-300">{r.currency} {(r.amount_fcy/1000).toFixed(0)}K</td>
      <td className="px-3 py-2.5">
        <div>
          <span className={days < 0 ? 'text-red-400' : days <= 30 ? 'text-amber-400' : 'text-gray-300'}>{fmtDateShort(effDate)}</span>
          {r.lc_type === 'Usance' && r.usance_period_days && <div className="text-purple-400 text-xs">+{r.usance_period_days}d usance</div>}
          {!settled && days >= 0 && days <= 30 && <div className="text-amber-400 text-xs">⚠ {days}d</div>}
          {!settled && days < 0  && <div className="text-red-500 text-xs">{Math.abs(days)}d overdue</div>}
        </div>
      </td>
      <td className="px-3 py-2.5 text-gray-300">{r.issuing_bank}</td>
      <td className="px-3 py-2.5">
        <div className="font-medium text-white">{r.commission_rate}% p.a.</div>
        <div className="text-gray-500">{fmtL(pricing)} total</div>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-col gap-1">
        <span className={`px-2 py-0.5 rounded-full text-xs w-fit ${HEDGE_BADGE[hedgeStatus] || ''}`}>{hedgeStatus}</span>
{!archived && !settled && hedgeStatus !== 'Fully Hedged' && onAddHedge && (
  <button onClick={() => onAddHedge(r.lc_ref)} className="text-xs text-teal-400 hover:text-teal-300 text-left">
    {hedgeStatus === 'Partially Hedged' ? '+ Add More Hedge' : '+ Add Hedge'}
  </button>
)}
        </div>
      </td>
      <td className="px-3 py-2.5">
        {risk > 0 ? (
          <div>
            <div className="flex items-center gap-1.5 mb-0.5">
              <div className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full ${risk > 2000000 ? 'bg-red-500' : 'bg-amber-500'} rounded-full`} style={{ width: `${Math.min(100, risk/5000000*100)}%` }} />
              </div>
              <span className="text-gray-400 text-xs">{fmtL(risk)}</span>
            </div>
            <div className="text-gray-600 text-xs">Unhedged × Spot × 2%</div>
          </div>
        ) : <span className="text-gray-600">—</span>}
      </td>
      <td className="px-3 py-2.5 text-gray-300 text-xs">{fmtCr(cf)}</td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1 flex-wrap">
          {!archived && !settled && days >= 0 && days <= 30 && (<>
            <button onClick={() => onAction(r, 'settle')} className="px-2 py-1 text-xs bg-teal-700 hover:bg-teal-600 text-white rounded-lg">Settle</button>
            <button onClick={() => onAction(r, 'bc')}     className="px-2 py-1 text-xs bg-amber-700 hover:bg-amber-600 text-amber-100 rounded-lg">BC</button>
          </>)}
          {!archived && !settled && days > 30 && (
            <button onClick={() => onAction(r, 'both')} className="px-2 py-1 text-xs border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700">Options</button>
          )}
          <button onClick={() => onEdit(r)}   className="px-2 py-1 text-xs border border-gray-700 text-gray-400 rounded-lg hover:text-white hover:border-gray-500">Edit</button>
          <button onClick={() => onDelete(r)} className="px-2 py-1 text-xs border border-red-900 text-red-500 rounded-lg hover:bg-red-900/30">Del</button>
        </div>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────
// BC SECTION
// ─────────────────────────────────────────────────────────────
function BCSection({ records, archived, fxRates, interestRates, hedgePositions, onEdit, onDelete, onAddHedge }) {
  const { rates: localFx } = useFxRates();
  const fx = Object.keys(fxRates).length > 0 ? fxRates : localFx;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const filtered = records.filter(r => {
    const d = r.repayment_date;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo   && d > dateTo)   return false;
    return true;
  });

  const active           = filtered.filter(r => r.status !== 'Settled');
  const totalOutstanding = active.reduce((s, r) => s + r.amount_fcy * resolveSpot(r.currency, fx, 0), 0);
  const totalInterest    = active.reduce((s, r) => s + bcInterestInr(r.amount_fcy, r.currency, fx, 0, r.all_in_rate, r.tenor_days), 0);
  const avgAllin         = active.length ? active.reduce((s, r) => s + (r.all_in_rate || 0), 0) / active.length : 0;
  const convertedCount   = active.filter(r => r.lc_id).length;

  function exportExcel() {
    const rows = filtered.map(b => ({
      'BC Ref':                    b.bc_ref,
      'Linked LC Ref':             b.lc_ref || '',
      'Lender Bank':               b.lender_bank,
      'Currency':                  b.currency,
      'Amount (FCY)':              b.amount_fcy,
      'Drawdown Date':             b.drawdown_date,
      'Repayment Date':            b.repayment_date,
      'Tenor (days)':              b.tenor_days,
      'Index':                     b.index_type,
      'Benchmark Rate (%)':        b.index_rate,
      'Spread (%)':                b.spread_rate,
      'All-in Rate (%)':           b.all_in_rate,
      'Live Spot Rate (INR)':      resolveSpot(b.currency, fx, 0),
      'Interest Cost (INR)':       Math.round(bcInterestInr(b.amount_fcy, b.currency, fx, 0, b.all_in_rate, b.tenor_days)),
      'Hedge Status':              b.hedge_status,
      'Hedged Amount (FCY)':       b.hedged_amount_fcy,
      'Contracted Rate (INR)':     b.contracted_rate_inr || '',
      'FX Risk (INR) @ 2% stress': Math.round(calcFxRiskInr(b.amount_fcy, b.hedged_amount_fcy, b.currency, fx, 0)),
      'Est. Cash Flow (INR)':      Math.round(calcBcCfInr(b, fx, interestRates)),
      'Status':                    b.status,
    }));
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0]).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BC Records');
    XLSX.writeFile(wb, `Nebula_BC_${archived ? 'archived' : 'active'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  return (
    <div>
      {!archived && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total BC Outstanding', value: fmtCr(totalOutstanding), sub: `${active.length} active` },
            { label: 'Avg All-in Cost',       value: `${avgAllin.toFixed(2)}%`, sub: 'benchmark + spread' },
            { label: 'Total Interest Cost',   value: fmtL(totalInterest),    sub: 'across active BCs' },
            { label: 'Converted from LC',     value: `${convertedCount} of ${active.length}`, sub: 'BC drawn against LC' },
          ].map(k => (
            <div key={k.label} className="bg-gray-800 rounded-xl p-4">
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">{k.label}</div>
              <div className="text-xl font-semibold text-white">{k.value}</div>
              <div className="text-xs text-gray-500 mt-1">{k.sub}</div>
            </div>
          ))}
        </div>
      )}

      {archived && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-900/20 border border-amber-800 rounded-xl mb-4 text-xs text-amber-400">
          📁 Showing archived BCs — settled and expired records
        </div>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <span className="text-sm font-medium text-white">{archived ? 'Archived BC Records' : 'Buyers Credit Records'}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Repayment:</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded-lg text-white" />
            <span className="text-xs text-gray-500">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded-lg text-white" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs text-red-400 hover:text-red-300">✕ Clear</button>
            )}
            <button onClick={exportExcel} className="px-2 py-1 text-xs border border-gray-700 text-gray-300 hover:text-white rounded-lg">↓ Excel</button>
            <span className="text-xs text-gray-500">{filtered.length} records</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-800 text-gray-400 text-left">
                <th className="px-3 py-2.5 font-medium">BC Ref</th>
                <th className="px-3 py-2.5 font-medium">Linked LC</th>
                <th className="px-3 py-2.5 font-medium">Lender</th>
                <th className="px-3 py-2.5 font-medium">Amount</th>
                <th className="px-3 py-2.5 font-medium">Repayment</th>
                <th className="px-3 py-2.5 font-medium">Tenor</th>
                <th className="px-3 py-2.5 font-medium">Index</th>
                <th className="px-3 py-2.5 font-medium">Benchmark</th>
                <th className="px-3 py-2.5 font-medium">Spread</th>
                <th className="px-3 py-2.5 font-medium">All-in</th>
                <th className="px-3 py-2.5 font-medium">Int. Cost</th>
                <th className="px-3 py-2.5 font-medium">Hedge</th>
                <th className="px-3 py-2.5 font-medium" title="Unhedged FCY × Live Spot × 2%">FX Risk ⓘ</th>
                <th className="px-3 py-2.5 font-medium">Est. CF</th>
                <th className="px-3 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={15} className="px-4 py-8 text-center text-gray-500">{archived ? 'No archived BCs' : 'No active BCs'}</td></tr>
              ) : filtered.map(b => (
               <BCRow key={b.id} b={b} fx={fx} interestRates={interestRates} archived={archived}
  hedgePositions={hedgePositions} onEdit={onEdit} onDelete={onDelete} onAddHedge={onAddHedge} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BCRow({ b, fx, interestRates, archived, hedgePositions, onEdit, onDelete, onAddHedge }) {
  const days        = daysToDate(b.repayment_date);
  const hedgeStatus = deriveHedgeStatus(b, hedgePositions, 'bc');
  const hedgedAmt   = deriveHedgedAmount(b, hedgePositions, 'bc');
  const interest    = bcInterestInr(b.amount_fcy, b.currency, fx, 0, b.all_in_rate, b.tenor_days);
  const risk        = calcFxRiskInr(b.amount_fcy, hedgedAmt, b.currency, fx, 0);
  const cf          = calcCfFromHedges(b, hedgePositions, fx, interestRates, 'bc');
  const idxColor    = INDEX_BADGE_COLORS[b.index_type] || INDEX_BADGE_COLORS.Fixed;

  return (
    <tr className={`border-t border-gray-800 hover:bg-gray-800/40 transition-colors ${archived ? 'opacity-60' : ''}`}>
      <td className="px-3 py-2.5 font-medium text-white">{b.bc_ref}</td>
      <td className="px-3 py-2.5 text-purple-400">{b.lc_ref || '—'}</td>
      <td className="px-3 py-2.5 text-gray-300">{b.lender_bank}</td>
      <td className="px-3 py-2.5 text-gray-300">{b.currency} {(b.amount_fcy/1000).toFixed(0)}K</td>
      <td className="px-3 py-2.5">
        <div>
          <span className={days < 0 ? 'text-red-400' : days <= 30 ? 'text-amber-400' : 'text-gray-300'}>{fmtDateShort(b.repayment_date)}</span>
          {!archived && days >= 0 && days <= 30 && <div className="text-amber-400 text-xs">⚠ {days}d</div>}
          {!archived && days < 0  && <div className="text-red-500 text-xs">{Math.abs(days)}d overdue</div>}
        </div>
      </td>
      <td className="px-3 py-2.5 text-gray-300">{b.tenor_days}d</td>
      <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs ${idxColor}`}>{b.index_type}</span></td>
      <td className="px-3 py-2.5 text-gray-300">{b.index_rate?.toFixed(2)}%</td>
      <td className="px-3 py-2.5 text-gray-300">+{b.spread_rate?.toFixed(2)}%</td>
      <td className="px-3 py-2.5 font-medium text-white">{b.all_in_rate?.toFixed(2)}%</td>
      <td className="px-3 py-2.5 text-amber-400">{fmtL(interest)}</td>
 <td className="px-3 py-2.5">
  <div className="flex flex-col gap-1">
    <span className={`px-2 py-0.5 rounded-full text-xs w-fit ${HEDGE_BADGE[hedgeStatus] || ''}`}>{hedgeStatus}</span>
   {!archived && hedgeStatus !== 'Fully Hedged' && onAddHedge && (
  <button onClick={() => onAddHedge(b.bc_ref)} className="text-xs text-teal-400 hover:text-teal-300 text-left">
    {hedgeStatus === 'Partially Hedged' ? '+ Add More Hedge' : '+ Add Hedge'}
  </button>
)}
  </div>
</td>
      <td className="px-3 py-2.5">
        {risk > 0 ? (
          <div>
            <div className="flex items-center gap-1.5 mb-0.5">
              <div className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full ${risk > 2000000 ? 'bg-red-500' : 'bg-amber-500'} rounded-full`} style={{ width: `${Math.min(100, risk/5000000*100)}%` }} />
              </div>
              <span className="text-gray-400 text-xs">{fmtL(risk)}</span>
            </div>
            <div className="text-gray-600 text-xs">Unhedged × Spot × 2%</div>
          </div>
        ) : <span className="text-gray-600">—</span>}
      </td>
      <td className="px-3 py-2.5 text-gray-300 text-xs">{fmtCr(cf)}</td>
      <td className="px-3 py-2.5">
        <div className="flex gap-1">
          <button onClick={() => onEdit(b)}   className="px-2 py-1 text-xs border border-gray-700 text-gray-400 rounded-lg hover:text-white hover:border-gray-500">Edit</button>
          <button onClick={() => onDelete(b)} className="px-2 py-1 text-xs border border-red-900 text-red-500 rounded-lg hover:bg-red-900/30">Del</button>
        </div>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────
// EDIT LC INLINE MODAL
// ─────────────────────────────────────────────────────────────
function EditLCInline({ lc, onClose, onSaved }) {
  const [form, setForm] = useState({
    lc_ref: lc.lc_ref||'', lc_type: lc.lc_type||'Sight', beneficiary: lc.beneficiary||'',
    issuing_bank: lc.issuing_bank||'', currency: lc.currency||'USD', amount_fcy: lc.amount_fcy||'',
    issue_date: lc.issue_date||'', maturity_date: lc.maturity_date||'',
    usance_period_days: lc.usance_period_days||90, usance_from: lc.usance_from||'Bill of Lading',
    commission_rate: lc.commission_rate||'', issuance_fee_inr: lc.issuance_fee_inr||'',
    swift_fee_inr: lc.swift_fee_inr||'', amendment_fee_inr: lc.amendment_fee_inr||'',
    negotiation_fee_inr: lc.negotiation_fee_inr||'', other_fee_inr: lc.other_fee_inr||'',
    hedge_status: lc.hedge_status||'Unhedged', hedged_amount_fcy: lc.hedged_amount_fcy||'',
    contracted_rate_inr: lc.contracted_rate_inr||'',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const set      = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isUsance = form.lc_type === 'Usance';

  const effectivePayDate = (() => {
    if (!isUsance || !form.maturity_date || !form.usance_period_days) return null;
    const d = new Date(form.maturity_date);
    d.setDate(d.getDate() + parseInt(form.usance_period_days));
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  })();

  async function handleSave() {
    if (!form.lc_ref || !form.beneficiary || !form.amount_fcy || !form.issue_date || !form.maturity_date || !form.commission_rate || !form.issuance_fee_inr) {
      setError('Please fill all required fields.'); return;
    }
    setSaving(true); setError('');
    const { error: err } = await supabase.from('import_financing').update({
      lc_ref: form.lc_ref, lc_type: form.lc_type, beneficiary: form.beneficiary,
      issuing_bank: form.issuing_bank, currency: form.currency, amount_fcy: parseFloat(form.amount_fcy),
      issue_date: form.issue_date, maturity_date: form.maturity_date,
      usance_period_days: isUsance ? parseInt(form.usance_period_days) : null,
      usance_from: isUsance ? form.usance_from : null,
      commission_rate: parseFloat(form.commission_rate),
      issuance_fee_inr: parseFloat(form.issuance_fee_inr)||0,
      swift_fee_inr: parseFloat(form.swift_fee_inr)||0,
      amendment_fee_inr: parseFloat(form.amendment_fee_inr)||0,
      negotiation_fee_inr: parseFloat(form.negotiation_fee_inr)||0,
      other_fee_inr: parseFloat(form.other_fee_inr)||0,
      hedge_status: form.hedge_status,
      hedged_amount_fcy: parseFloat(form.hedged_amount_fcy)||0,
      contracted_rate_inr: parseFloat(form.contracted_rate_inr)||null,
    }).eq('id', lc.id);
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  }

  return (
    <ModalShell title={`Edit LC — ${lc.lc_ref}`} subtitle={lc.beneficiary} onClose={onClose}>
      <Section>LC Details</Section>
      <Grid>
        <MF label="LC Reference *"  value={form.lc_ref}        onChange={v => set('lc_ref', v)}        placeholder="LC-2024-001" />
        <MS label="Type"            value={form.lc_type}       onChange={v => set('lc_type', v)}       options={LC_TYPES} />
        <MF label="Beneficiary *"   value={form.beneficiary}   onChange={v => set('beneficiary', v)}   placeholder="Supplier name" />
        <MF label="Issuing Bank"    value={form.issuing_bank}  onChange={v => set('issuing_bank', v)}  placeholder="HDFC Bank" />
        <MS label="Currency"        value={form.currency}      onChange={v => set('currency', v)}      options={CURRENCIES} />
        <MF label="Amount (FCY) *"  value={form.amount_fcy}    onChange={v => set('amount_fcy', v)}    type="number" />
        <MF label="Issue Date *"    value={form.issue_date}    onChange={v => set('issue_date', v)}    type="date" />
        <MF label="Maturity Date *" value={form.maturity_date} onChange={v => set('maturity_date', v)} type="date" />
      </Grid>
      {isUsance && (<>
        <Section color="text-purple-400 border-purple-900">Usance Terms</Section>
        <Grid>
          <MS label="Usance Period (days)" value={form.usance_period_days} onChange={v => set('usance_period_days', v)} options={USANCE_PERIODS.map(String)} />
          <MS label="Usance From"          value={form.usance_from}        onChange={v => set('usance_from', v)}        options={USANCE_FROM} />
        </Grid>
        {effectivePayDate && (
          <div className="flex items-center gap-2 px-3 py-2 bg-purple-900/30 border border-purple-800 rounded-lg mb-3 text-xs">
            <span className="text-purple-400">📅</span>
            <span className="text-gray-400">Effective Payment Date</span>
            <span className="text-white font-medium">{effectivePayDate}</span>
          </div>
        )}
      </>)}
      <Section>Pricing</Section>
      <Grid>
        <MF label="Commission Rate (% p.a.) *" value={form.commission_rate}     onChange={v => set('commission_rate', v)}     type="number" placeholder="0.75" />
        <MF label="Issuance Fee (₹) *"         value={form.issuance_fee_inr}    onChange={v => set('issuance_fee_inr', v)}    type="number" placeholder="15000" />
        <MF label="SWIFT Fee (₹)"              value={form.swift_fee_inr}       onChange={v => set('swift_fee_inr', v)}       type="number" />
        <MF label="Negotiation Fee (₹)"        value={form.negotiation_fee_inr} onChange={v => set('negotiation_fee_inr', v)} type="number" />
        <MF label="Amendment Fee (₹)"          value={form.amendment_fee_inr}   onChange={v => set('amendment_fee_inr', v)}   type="number" />
        <MF label="Other Charges (₹)"          value={form.other_fee_inr}       onChange={v => set('other_fee_inr', v)}       type="number" />
      </Grid>
      <Section>Hedge</Section>
      <Grid>
        <MS label="Hedge Status"          value={form.hedge_status}        onChange={v => set('hedge_status', v)}        options={HEDGE_STATUSES} />
        <MF label="Hedged Amount (FCY)"   value={form.hedged_amount_fcy}   onChange={v => set('hedged_amount_fcy', v)}   type="number" disabled={form.hedge_status === 'Unhedged'} />
        <MF label="Contracted Rate (INR)" value={form.contracted_rate_inr} onChange={v => set('contracted_rate_inr', v)} type="number" disabled={form.hedge_status === 'Unhedged'} />
      </Grid>
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <ModalActions onClose={onClose} onSave={handleSave} saving={saving} saveLabel="Save Changes" />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────
// EDIT BC INLINE MODAL
// ─────────────────────────────────────────────────────────────
function EditBCInline({ bc, interestRates, onClose, onSaved }) {
  const [form, setForm] = useState({
    bc_ref: bc.bc_ref||'', lc_ref: bc.lc_ref||'', lender_bank: bc.lender_bank||'',
    currency: bc.currency||'USD', amount_fcy: bc.amount_fcy||'',
    drawdown_date: bc.drawdown_date||'', repayment_date: bc.repayment_date||'',
    tenor_days: bc.tenor_days||90, index_type: bc.index_type||'SOFR',
    index_rate: bc.index_rate||'', spread_rate: bc.spread_rate||'',
    all_in_rate: bc.all_in_rate||'', hedge_status: bc.hedge_status||'Unhedged',
    hedged_amount_fcy: bc.hedged_amount_fcy||'', contracted_rate_inr: bc.contracted_rate_inr||'',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const bm  = BENCHMARKS[form.currency] || { options: ['Fixed'] };

  useEffect(() => {
    const idx = parseFloat(form.index_rate) || 0;
    const spd = parseFloat(form.spread_rate) || 0;
    if (form.index_type === 'Fixed') {
      if (idx > 0) setForm(f => ({ ...f, all_in_rate: idx.toFixed(2) }));
    } else {
      if (idx > 0 || spd > 0) setForm(f => ({ ...f, all_in_rate: (idx + spd).toFixed(2) }));
    }
  }, [form.index_rate, form.spread_rate, form.index_type]); // eslint-disable-line

  async function handleSave() {
    if (!form.bc_ref || !form.lender_bank || !form.amount_fcy || !form.all_in_rate) {
      setError('Please fill all required fields.'); return;
    }
    setSaving(true); setError('');
    const { error: err } = await supabase.from('import_financing_bc').update({
      bc_ref: form.bc_ref, lc_ref: form.lc_ref||null, lender_bank: form.lender_bank,
      currency: form.currency, amount_fcy: parseFloat(form.amount_fcy),
      drawdown_date: form.drawdown_date||null, repayment_date: form.repayment_date||null,
      tenor_days: parseInt(form.tenor_days), index_type: form.index_type,
      index_rate: parseFloat(form.index_rate)||null, spread_rate: parseFloat(form.spread_rate)||null,
      all_in_rate: parseFloat(form.all_in_rate),
      hedge_status: form.hedge_status, hedged_amount_fcy: parseFloat(form.hedged_amount_fcy)||0,
      contracted_rate_inr: parseFloat(form.contracted_rate_inr)||null,
    }).eq('id', bc.id);
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  }

  return (
    <ModalShell title={`Edit BC — ${bc.bc_ref}`} subtitle={bc.lender_bank} onClose={onClose}>
      <Section>BC Details</Section>
      <Grid>
        <MF label="BC Reference *"  value={form.bc_ref}         onChange={v => set('bc_ref', v)}         placeholder="BC-2024-001" />
        <MF label="Linked LC Ref"   value={form.lc_ref}         onChange={v => set('lc_ref', v)}         placeholder="LC-2024-001" />
        <MF label="Lender Bank *"   value={form.lender_bank}    onChange={v => set('lender_bank', v)}    placeholder="DBS Bank" />
        <MS label="Currency"        value={form.currency}       onChange={v => set('currency', v)}       options={CURRENCIES} />
        <MF label="Amount (FCY) *"  value={form.amount_fcy}     onChange={v => set('amount_fcy', v)}     type="number" />
        <MF label="Drawdown Date"   value={form.drawdown_date}  onChange={v => set('drawdown_date', v)}  type="date" />
        <MF label="Tenor (days)"    value={form.tenor_days}     onChange={v => set('tenor_days', v)}     type="number" />
        <MF label="Repayment Date"  value={form.repayment_date} onChange={v => set('repayment_date', v)} type="date" />
      </Grid>
      <Section>Pricing</Section>
      <Grid>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Index ({form.currency}-linked)</label>
          <select value={form.index_type}
            onChange={e => {
              const sel = e.target.value;
              const rcMap = { 'SOFR':'FED_FUNDS','€STR':'ECB_DEPOSIT','SONIA':'BOE_BASE','SORA':'MAS_SORA' };
              const rc   = rcMap[sel];
              const rate = rc && interestRates[rc] ? interestRates[rc].overnightRate : '';
              setForm(f => ({ ...f, index_type: sel, index_rate: sel === 'Fixed' ? '' : rate }));
            }}
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white">
            {bm.options.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">{form.index_type !== 'Fixed' ? 'Indicative Benchmark Rate (%)' : 'Fixed Rate (%)'}</label>
          <input type="number" step="0.01" value={form.index_rate} disabled={form.index_type !== 'Fixed'}
            onChange={e => set('index_rate', e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-60" />
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Spread (%) {form.index_type === 'Fixed' && <span className="text-gray-600">— n/a</span>}</label>
          <input type="number" step="0.01" value={form.spread_rate} disabled={form.index_type === 'Fixed'}
            onChange={e => set('spread_rate', e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-40" />
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">All-in Cost (%) *</label>
          <input type="number" step="0.01" value={form.all_in_rate} onChange={e => set('all_in_rate', e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white font-medium" />
        </div>
      </Grid>
      <Section>Hedge</Section>
      <Grid>
        <MS label="Hedge Status"          value={form.hedge_status}        onChange={v => set('hedge_status', v)}        options={HEDGE_STATUSES} />
        <MF label="Hedged Amount (FCY)"   value={form.hedged_amount_fcy}   onChange={v => set('hedged_amount_fcy', v)}   type="number" disabled={form.hedge_status === 'Unhedged'} />
        <MF label="Contracted Rate (INR)" value={form.contracted_rate_inr} onChange={v => set('contracted_rate_inr', v)} type="number" disabled={form.hedge_status === 'Unhedged'} />
      </Grid>
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <ModalActions onClose={onClose} onSave={handleSave} saving={saving} saveLabel="Save Changes" saveBg="bg-purple-700 hover:bg-purple-600" />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────
// DELETE CONFIRM
// ─────────────────────────────────────────────────────────────
function DeleteConfirm({ type, record, onClose, onSaved }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState('');
  const isLC  = type === 'lc';
  const table = isLC ? 'import_financing' : 'import_financing_bc';
  const ref   = isLC ? record.lc_ref : record.bc_ref;

  async function handleDelete() {
    setDeleting(true);
    const { error: err } = await supabase.from(table).delete().eq('id', record.id);
    setDeleting(false);
    if (err) { setError(err.message); return; }
    onSaved();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-[400px]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-900/40 border border-red-800 flex items-center justify-center text-red-400 text-lg">✕</div>
          <div>
            <h2 className="text-base font-semibold text-white">Delete {isLC ? 'LC' : 'BC'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">This cannot be undone</p>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-3 mb-5">
          <div className="font-medium text-white text-sm">{ref}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {isLC ? record.beneficiary : record.lender_bank} · {record.currency} {(record.amount_fcy/1000).toFixed(0)}K
          </div>
        </div>
        {isLC && <p className="text-xs text-amber-400 mb-4">⚠ Any linked Buyers Credit records will lose their LC reference.</p>}
        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-700 text-gray-400 rounded-lg hover:text-gray-200">Cancel</button>
          <button onClick={handleDelete} disabled={deleting}
            className="px-4 py-2 text-sm bg-red-700 hover:bg-red-600 text-white rounded-lg disabled:opacity-50">
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SHARED PRIMITIVES — outside components to prevent re-mount
// ─────────────────────────────────────────────────────────────
function ModalShell({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-[560px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-white">{title}</h2>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Section({ children, color = 'text-gray-400 border-gray-800' }) {
  return <div className={`text-xs font-medium ${color} uppercase tracking-wider border-b pb-1 mb-3 mt-4`}>{children}</div>;
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 gap-3 mb-2">{children}</div>;
}

function MF({ label, value, onChange, type = 'text', placeholder = '', disabled = false }) {
  return (
    <div>
      <label className="text-xs text-gray-400 block mb-1">{label}</label>
      <input type={type} value={value ?? ''} placeholder={placeholder} disabled={disabled}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-40" />
    </div>
  );
}

function MS({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs text-gray-400 block mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white">
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

function ModalActions({ onClose, onSave, saving, saveLabel = 'Save', saveBg = 'bg-teal-600 hover:bg-teal-500' }) {
  return (
    <div className="flex gap-3 justify-end mt-4">
      <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-700 text-gray-400 rounded-lg hover:text-gray-200">Cancel</button>
      <button onClick={onSave} disabled={saving} className={`px-4 py-2 text-sm ${saveBg} text-white rounded-lg disabled:opacity-50 transition-colors`}>
        {saving ? 'Saving...' : saveLabel}
      </button>
    </div>
  );
}
